/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { applyDataStateOverride, useDataStateOverride } from '@/shared/ui';
import type { AgencyValuesType } from '@pages/authenticated/agency-pages/AgenciesContext';

/** Settings surface data-flags plus the resolved agency for rendering. */
export interface SettingsData {
  /** The active agency from the shared context store. */
  agency: AgencyValuesType;
  /** True until the agency has been fetched (no `id` yet), or while forced. */
  isLoading: boolean;
  /** True when the settings fetch failed, or while forced to error. */
  isError: boolean;
  /** True when the resolved agency carries no data. */
  isEmpty: boolean;
}

/**
 * Resolves the settings surface data-flags from the shared agency context and
 * ORs in the Dev Cockpit override at the data-flag layer, so the early-return
 * `DataState` gate honors a forced lifecycle even after the agency loaded.
 *
 * @returns The agency plus effective `{ isLoading, isError, isEmpty }` flags.
 */
export function useSettingsData(): SettingsData {
  const agency = useAgency((state) => state.agency);
  const override = useDataStateOverride();
  const flags = applyDataStateOverride(override, {
    isLoading: !agency.id,
    isEmpty: !agency.id,
  });
  return {
    agency,
    isLoading: flags.isLoading,
    isError: flags.isError,
    isEmpty: flags.isEmpty,
  };
}
